import type { GlanceRow, StorySectionKey } from "@/lib/orgStory";
import SurfaceCard from "@/components/primitives/SurfaceCard";
import { STORY_SECTION_LABELS, storySectionAnchor } from "./storyLabels";

/** The fewest rows worth a card (spec 2026-10-01 §4.1). */
const MIN_ROWS = 2;

/** Reads `document` only inside the handler, so the card is safe to prerender. */
function scrollToSection(section: StorySectionKey) {
  const el = document.getElementById(storySectionAnchor(section));
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.focus({ preventScroll: true });
}

/**
 * The story rail's "At a glance" card: the offer in a few one-line rows, the
 * same for signed-in and signed-out readers. A row whose section is on the
 * page scrolls to it.
 */
export default function AtAGlanceCard({ rows, sections }: { rows: GlanceRow[]; sections: StorySectionKey[] }) {
  if (rows.length < MIN_ROWS) return null;

  return (
    <SurfaceCard padding="md" className="rounded-2xl">
      <p className="eyebrow text-[10px] text-ink-500">At a glance</p>
      <dl className="mt-3 flex flex-col gap-2.5">
        {rows.map((row) => (
          <div key={row.key} className="grid grid-cols-[68px_minmax(0,1fr)] items-baseline gap-3 text-[13px]">
            <dt className="text-ink-500">{row.label}</dt>
            <dd className="min-w-0 font-medium text-ink-900">
              {sections.includes(row.section) ? (
                <button
                  type="button"
                  aria-label={`${row.value} — go to ${STORY_SECTION_LABELS[row.section].pill}`}
                  onClick={() => scrollToSection(row.section)}
                  className="block w-full break-words rounded-sm text-left hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {row.value}
                </button>
              ) : (
                <span className="block break-words">
                  {row.value}
                </span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </SurfaceCard>
  );
}
